import { industryMapping } from './mappings';

const formatPreviewValue = (value) => {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Intl.NumberFormat().format(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    const numericValue = Number(trimmed.replace(/,/g, ''));
    if (!Number.isNaN(numericValue) && Number.isFinite(numericValue)) {
      return new Intl.NumberFormat().format(numericValue);
    }

    return trimmed;
  }

  return String(value);
};

const formatCraftLocation = (item) => {
  const directCraftable = item?.craftable || item?.whereToGet;
  if (directCraftable) {
    const normalized = String(directCraftable).trim().toLowerCase();
    return industryMapping[normalized] || directCraftable;
  }

  const recipeSkill = item?.recipes?.[0]?.requiredSkill || item?.recipes?.[0]?.skill;
  if (recipeSkill) {
    const normalized = String(recipeSkill).trim().toLowerCase();
    return industryMapping[normalized] || recipeSkill;
  }

  return null;
};

const formatSourceText = (item) => {
  const obtainSources = Array.isArray(item?.notes?.[0]?.obtain) ? item.notes[0].obtain.filter(Boolean) : [];
  const storeSources = Array.isArray(item?.stores) ? item.stores.map((store) => store?.store).filter(Boolean) : [];
  const sources = [...obtainSources, ...storeSources];

  if (sources.length === 0) return null;
  return `Available from ${[...new Set(sources)].join(', ')}`;
};

const formatRecipeText = (item) => {
  const recipe = Array.isArray(item?.recipes) && item.recipes.length > 0 ? item.recipes[0] : null;
  if (!recipe) return null;

  if (typeof recipe === 'string') return `Crafting: ${recipe}`;
  if (typeof recipe === 'object') {
    const recipeName = recipe.name || recipe.title;
    if (!recipeName) return null;
    return `Crafting: ${recipeName}`;
  }

  return null;
};

export const buildItemSocialMeta = (item, url) => {
  const safeUrl = url || 'https://pixelore.wiki/';
  const name = item?.name;
  const pixeloreDescription = item?.notes?.[0]?.description;
  const gameDescription = item?.description;

  const detailParts = [];
  if (gameDescription) detailParts.push(`Game: ${gameDescription}`);
  if (pixeloreDescription) detailParts.push(`PixeLore: ${pixeloreDescription}`);
  const detail = detailParts.length > 0
    ? detailParts.join(' | ')
    : 'Explore item details, recipes, and market data on PixeLore.';
  const latestHigh = formatPreviewValue(item?.marketHistory?.['15D']?.value || item?.marketHistory?.['7D']?.value || item?.marketHistory?.['1D']?.value);
  const latestLow = formatPreviewValue(item?.marketHistory?.['1D']?.value || item?.marketHistory?.['7D']?.value || item?.marketHistory?.['15D']?.value);
  const craftLocation = formatCraftLocation(item);
  const sourceText = formatSourceText(item);
  const recipeText = formatRecipeText(item);
  const image = item?.image || item?.itemImage || 'https://pixelore.wiki/pixelore-social-preview.png';
  const id = item?.id || '';

  const richDetailParts = [detail.replace(/\s+/g, ' ').trim()];
  if (craftLocation) {
    richDetailParts.push(`Craftable in ${craftLocation}`);
  }
  if (sourceText) {
    richDetailParts.push(sourceText);
  }
  if (latestHigh) {
    richDetailParts.push(`High: ${latestHigh}`);
  }
  if (latestLow) {
    richDetailParts.push(`Low: ${latestLow}`);
  }
  if (recipeText) {
    richDetailParts.push(recipeText);
  }

  const description = richDetailParts.join(' • ').slice(0, 220);

  return {
    title: name ? `${name} | PixeLore` : 'Item Lookup | PixeLore',
    description,
    image: image.startsWith('http') ? image : `https://pixelore.wiki${image.startsWith('/') ? image : `/${image}`}`,
    url: safeUrl,
    id,
  };
};

export const applySocialMeta = (meta) => {
  if (typeof document === 'undefined') return;

  document.title = meta?.title || 'PixeLore | The Ultimate Pixels Web3 Wiki';

  const setMetaTag = (attr, value, content) => {
    if (!value || !content) return;
    let tag = document.querySelector(`meta[${attr}="${value}"]`);
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute(attr, value);
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
  };

  setMetaTag('property', 'og:title', meta?.title);
  setMetaTag('property', 'og:description', meta?.description);
  setMetaTag('property', 'og:image', meta?.image);
  setMetaTag('property', 'og:url', meta?.url);
  setMetaTag('name', 'twitter:title', meta?.title);
  setMetaTag('name', 'twitter:description', meta?.description);
  setMetaTag('name', 'twitter:image', meta?.image);
  setMetaTag('name', 'twitter:url', meta?.url);

  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', meta?.url || 'https://pixelore.wiki/');
  }
};
